import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { FiRefreshCw } from 'react-icons/fi';
import { RiDownload2Line } from 'react-icons/ri';
import { Alert, Button, Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';
import DateRangeFilter from 'pages/Notifications/components/AllNotification/DateRangePicker';
import moment from 'moment';
import './style.css';

const API_BASE_URL = (
  process.env.REACT_APP_REST_API_ENDPOINT ||
  (process.env.REACT_APP_API_BASE_URL
    ? `${process.env.REACT_APP_API_BASE_URL}/api/`
    : '/api/')
).replace(/\/+$/, '');

const ACTIVE_USER_METRICS = [
  { key: 'dau', title: 'Daily Active Users', exportType: 'DAU' },
  { key: 'wau', title: 'Weekly Active Users', exportType: 'WAU' },
  { key: 'mau', title: 'Monthly Active Users', exportType: 'MAU' },
];

const SUMMARY_METRICS = [
  { key: 'totalActiveUsers', title: 'Total Active Users' },
  { key: 'newActivations', title: 'New Activations' },
  { key: 'appUninstalls', title: 'App Uninstalls' },
];

const PLATFORM_LABELS = {
  android: 'Android',
  ios: 'iOS',
  web: 'Web',
};

const monthFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
});

const cloneDate = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatDate = (date) => monthFormatter.format(date);

const formatRangeLabel = (from, to) => {
  if (from.getTime() === to.getTime()) return formatDate(from);
  return `${formatDate(from)} To ${formatDate(to)}`;
};

const getMonday = (date) => {
  const monday = cloneDate(date);
  const day = monday.getDay();
  monday.setDate(monday.getDate() - (day === 0 ? 6 : day - 1));
  return monday;
};

const getMetricPeriods = () => {
  const today = cloneDate(new Date());
  const yesterday = cloneDate(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const thisWeekStart = getMonday(today);
  const lastWeekEnd = cloneDate(thisWeekStart);
  lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
  const lastWeekStart = cloneDate(lastWeekEnd);
  lastWeekStart.setDate(lastWeekStart.getDate() - 6);

  const last30DaysStart = cloneDate(today);
  last30DaysStart.setDate(last30DaysStart.getDate() - 30);
  const previous30DaysEnd = cloneDate(today);
  previous30DaysEnd.setDate(previous30DaysEnd.getDate() - 31);
  const previous30DaysStart = cloneDate(today);
  previous30DaysStart.setDate(previous30DaysStart.getDate() - 60);

  return {
    dau: {
      current: { label: 'Today', from: today, to: today },
      previous: { label: 'Yesterday', from: yesterday, to: yesterday },
    },
    wau: {
      current: { label: 'This week', from: thisWeekStart, to: today },
      previous: { label: 'Last week', from: lastWeekStart, to: lastWeekEnd },
    },
    mau: {
      current: { label: 'Last 30 Days', from: last30DaysStart, to: today },
      previous: {
        label: 'Previous 30 Days',
        from: previous30DaysStart,
        to: previous30DaysEnd,
      },
    },
    summary: { from: last30DaysStart, to: today },
  };
};

const numberOrZero = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeSplit = (split = {}) => ({
  android: numberOrZero(split.android),
  ios: numberOrZero(split.ios),
  web: numberOrZero(split.web),
});

const formatApiDate = (value) => {
  const date = moment(value, 'YYYY-MM-DD', true);
  return date.isValid() ? date.format('MMM DD, YYYY') : value;
};

const formatApiRange = (range, fallback) => {
  if (!range) return fallback;
  const dates = String(range).split(/\s+to\s+/i);
  if (dates.length !== 2) return fallback;
  return `${formatApiDate(dates[0])} To ${formatApiDate(dates[1])}`;
};

const createPeriod = (period, total, split = {}) => ({
  label: period.label,
  dateLabel: formatRangeLabel(period.from, period.to),
  value: {
    total: numberOrZero(total),
    ...normalizeSplit(split),
  },
});

const buildMetricState = (data = {}) => {
  const periods = getMetricPeriods();
  const dau = data.dau || {};
  const wau = data.wau || {};
  const mau = data.mau || {};

  return {
    dau: {
      current: createPeriod(periods.dau.current, dau.today, dau.split),
      previous: createPeriod(periods.dau.previous, dau.yesterday),
    },
    wau: {
      current: {
        ...createPeriod(periods.wau.current, wau.currentWeek, wau.split),
        dateLabel: formatApiRange(
          wau.currentRange,
          formatRangeLabel(periods.wau.current.from, periods.wau.current.to),
        ),
      },
      previous: {
        ...createPeriod(periods.wau.previous, wau.previousWeek),
        dateLabel: formatApiRange(
          wau.previousRange,
          formatRangeLabel(periods.wau.previous.from, periods.wau.previous.to),
        ),
      },
    },
    mau: {
      current: {
        ...createPeriod(periods.mau.current, mau.currentMonth, mau.split),
        dateLabel: formatApiRange(
          mau.currentRange,
          formatRangeLabel(periods.mau.current.from, periods.mau.current.to),
        ),
      },
      previous: {
        ...createPeriod(periods.mau.previous, mau.previousMonth),
        dateLabel: formatApiRange(
          mau.previousRange,
          formatRangeLabel(periods.mau.previous.from, periods.mau.previous.to),
        ),
      },
    },
    totalActiveUsers: {
      current: {
        label: '(Last 30 days)',
        dateLabel: formatRangeLabel(periods.summary.from, periods.summary.to),
        value: { total: numberOrZero(data.totalActiveUsers) },
      },
    },
    newActivations: {
      current: {
        label: '(Last 30 days)',
        dateLabel: formatRangeLabel(periods.summary.from, periods.summary.to),
        value: { total: numberOrZero(data.newActivations) },
      },
    },
    appUninstalls: {
      current: {
        label: '(Last 30 days)',
        dateLabel: formatRangeLabel(periods.summary.from, periods.summary.to),
        value: {
          total:
            data.appUninstalls == null ? null : numberOrZero(data.appUninstalls),
        },
      },
    },
  };
};

const formatCount = (value) =>
  value == null ? '-' : numberOrZero(value).toLocaleString('en-IN');

const getUpdatedAt = (value) => {
  if (!value) return new Date();
  const normalizedValue = String(value).replace(/\[[^\]]+\]$/, '');
  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const ActiveUserMetrics = () => {
  const { auth } = useContext(authContext);
  const [metrics, setMetrics] = useState(() => buildMetricState());
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [exportingType, setExportingType] = useState(null);

  const handleExport = async (exportType, metricTitle) => {
    if (exportingType) return;
    setExportingType(exportType);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/analytics/active-users-export`,
        {
          params: { type: exportType },
          headers: {
            Accept: '*/*',
            ...(auth?.data?.token
              ? { Authorization: `Bearer ${auth.data.token}` }
              : {}),
          },
        },
      );

      const responseData = response?.data;
      let downloadUrl = responseData?.downloadUrl;

      if (!downloadUrl) {
        throw new Error(
          responseData?.message || 'Download URL not received from server.',
        );
      }

      if (downloadUrl.startsWith('http://')) {
        downloadUrl = downloadUrl.replace(/^http:\/\//i, 'https://');
      } else if (!downloadUrl.startsWith('https://')) {
        downloadUrl = `${API_BASE_URL.replace(/\/api\/?$/, '')}/${downloadUrl.replace(/^\//, '')}`;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute(
        'download',
        responseData?.fileName || `active-users-${exportType}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(
        responseData?.message || `${metricTitle || exportType} CSV generated successfully.`,
      );
    } catch (error) {
      console.error(`Active users export failed for ${exportType}:`, error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          `Failed to export ${metricTitle || exportType} data.`,
      );
    } finally {
      setExportingType(null);
    }
  };

  const fetchMetrics = useCallback(async ({ refresh = false } = {}) => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/analytics/active-users-summary`,
        {
          params: {
            platform: 'all',
            ...(refresh ? { refresh: true } : {}),
          },
          headers: auth?.data?.token
            ? { Authorization: `Bearer ${auth.data.token}` }
            : {},
        },
      );
      const responseData = response?.data;

      if (responseData?.status !== true || !responseData?.data) {
        throw new Error(responseData?.message || 'Unable to load active users.');
      }

      setMetrics(buildMetricState(responseData.data));
      setLastUpdatedAt(getUpdatedAt(responseData.data.updatedAt));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Unable to load active users.',
      );
    } finally {
      setLoading(false);
    }
  }, [auth?.data?.token]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const getUpdatedText = () => {
    if (!lastUpdatedAt) return loading ? 'Loading...' : '';
    const timeString = lastUpdatedAt.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    return loading
      ? `Refreshing... (Last updated ${timeString})`
      : `Updated ${timeString}`;
  };

  const handleRefresh = () => {
    setDateRange({ startDate: null, endDate: null });
    fetchMetrics({ refresh: true });
  };

  const handleFilter = ({ startDate, endDate }) => {
    setDateRange({ startDate, endDate });
    if (startDate || endDate) {
      setErrorMessage(
        'This API returns preset daily, weekly, and monthly ranges; custom date filtering is unavailable.',
      );
      return;
    }
    fetchMetrics();
  };

  return (
    <div className="active-user-metrics mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3 active-user-metrics__topbar">
        <div
          className="active-user-metrics__header m-0 p-0"
          style={{ border: 'none', background: 'transparent' }}
        >
          <div className="active-user-metrics__updated">{getUpdatedText()}</div>
          <Button
            className="active-user-metrics__refresh"
            color="link"
            disabled={loading}
            onClick={handleRefresh}
            title="Refresh active user data"
            type="button"
          >
            <FiRefreshCw
              className={loading ? 'active-user-metrics__refresh-icon--spin' : ''}
            />
          </Button>
        </div>

        <DateRangeFilter dateRange={dateRange} onFilter={handleFilter} />
      </div>

      {errorMessage ? (
        <Alert color="warning" className="active-user-metrics__alert">
          {errorMessage}
        </Alert>
      ) : null}

      <Row>
        <Col xs={12} />
        {ACTIVE_USER_METRICS.map((metric) => (
          <Col lg={4} md={6} className="mb-3" key={metric.key}>
            <Card className="active-user-metrics__card">
              <CardBody>
                <div className="active-user-metrics__card-head">
                  <h3>{metric.title}</h3>
                  <Button
                    className="gray-btn csv-btn active-user-metrics__download-btn"
                    color="link"
                    size="sm"
                    type="button"
                    disabled={exportingType !== null}
                    onClick={() => handleExport(metric.exportType, metric.title)}
                    title={`Download ${metric.title} CSV`}
                  >
                    {exportingType === metric.exportType ? (
                      <Spinner
                        color="primary"
                        size="sm"
                        style={{ width: '14px', height: '14px', borderWidth: '2px' }}
                      />
                    ) : (
                      <RiDownload2Line size={16} className="fw-bold" />
                    )}
                  </Button>
                </div>

                {['current', 'previous'].map((periodKey) => {
                  const period = metrics?.[metric.key]?.[periodKey];

                  return (
                    <div className="active-user-metrics__row" key={periodKey}>
                      <div>
                        <span>{period?.label}</span>
                        {period?.dateLabel && <small>{period.dateLabel}</small>}
                      </div>
                      <strong>{formatCount(period?.value?.total)}</strong>
                    </div>
                  );
                })}

                {['android', 'ios', 'web'].map((platform) => (
                  <div
                    className={`active-user-metrics__row${platform === 'android' ? ' mt-3' : ''}`}
                    key={platform}
                  >
                    <div>
                      <span>{PLATFORM_LABELS[platform]}</span>
                    </div>
                    <strong>
                      {formatCount(metrics?.[metric.key]?.current?.value?.[platform])}
                    </strong>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        ))}

        {SUMMARY_METRICS.map((metric) => {
          const period = metrics?.[metric.key]?.current;
          const isUnavailable =
            metric.key === 'appUninstalls' && period?.value?.total == null;

          return (
            <Col lg={4} md={6} className="mb-3" key={metric.key}>
              <Card className="active-user-metrics__card active-user-metrics__card--summary">
                <CardBody>
                  <div className="active-user-metrics__summary-row">
                    <div>
                      <h3>{metric.title}</h3>
                      <span>
                        {isUnavailable ? 'Not provided by API' : period?.label}
                      </span>
                      {!isUnavailable && period?.dateLabel && (
                        <small>{period.dateLabel}</small>
                      )}
                    </div>
                    <strong>{formatCount(period?.value?.total)}</strong>
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default ActiveUserMetrics;
